export class AuthRequiredError extends Error {
  constructor(message = 'Auth0 session is required.') {
    super(message);
    this.name = 'AuthRequiredError';
  }
}

export class GoogleAuthRequiredError extends Error {
  constructor(message = 'Google authentication is required.') {
    super(message);
    this.name = 'GoogleAuthRequiredError';
  }
}

export const AUTH_REQUIRED_EVENT = 'auth-required';

export function notifyAuthRequired(): void {
  window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
}

export type ErrorResponse = {
  error_code: string;
  message: string;
  request_id: string;
  details?: unknown;
  fields?: Record<string, string>;
};

export const REQUEST_ID_HEADER_KEYS = [
  'x-request-id',
  'x-request_id',
  'request-id',
] as const;

export const isErrorResponse = (value: unknown): value is ErrorResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.error_code === 'string' &&
    typeof record.message === 'string' &&
    typeof record.request_id === 'string'
  );
};

const isErrorResponsePayload = (
  value: unknown
): value is {
  error_code: string;
  message: string;
  request_id?: string;
  details?: unknown;
  fields?: unknown;
} => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record.error_code === 'string' && typeof record.message === 'string';
};

const parseFields = (fields: unknown): Record<string, string> | undefined => {
  if (!fields || typeof fields !== 'object') {
    return undefined;
  }
  const record = fields as Record<string, unknown>;
  const entries = Object.entries(record).filter(
    ([, value]) => typeof value === 'string'
  ) as Array<[string, string]>;
  if (entries.length === 0) {
    return undefined;
  }
  return Object.fromEntries(entries);
};

export const getRequestIdFromHeaders = (headers: Headers): string | null => {
  for (const key of REQUEST_ID_HEADER_KEYS) {
    const value = headers.get(key);
    if (value) {
      return value;
    }
  }
  return null;
};

export async function parseErrorResponse(
  response: Response
): Promise<ErrorResponse | null> {
  const body = await response.json().catch(() => null);
  if (!isErrorResponsePayload(body)) {
    return null;
  }
  const requestId =
    typeof body.request_id === 'string'
      ? body.request_id
      : getRequestIdFromHeaders(response.headers) ?? 'unknown';
  return {
    error_code: body.error_code,
    message: body.message,
    request_id: requestId,
    details: body.details,
    fields: parseFields(body.fields),
  };
}

export class AppError extends Error {
  error: ErrorResponse;
  status?: number;

  constructor(error: ErrorResponse, status?: number) {
    const requestSuffix = error.request_id
      ? ` (request_id: ${error.request_id})`
      : '';
    super(`${error.message}${requestSuffix}`);
    this.name = 'AppError';
    this.error = error;
    this.status = status;
  }
}

export function toAppError(error: unknown, status?: number): AppError {
  if (error instanceof AppError) {
    return error;
  }
  if (isErrorResponsePayload(error)) {
    return new AppError(
      {
        error_code: error.error_code,
        message: error.message,
        request_id: error.request_id ?? 'unknown',
        details: error.details,
        fields: parseFields(error.fields),
      },
      status
    );
  }
  if (error instanceof Error) {
    return new AppError(
      {
        error_code: 'UNKNOWN',
        message: error.message,
        request_id: 'unknown',
        details: error,
      },
      status
    );
  }
  return new AppError(
    {
      error_code: 'UNKNOWN',
      message: 'Unknown error',
      request_id: 'unknown',
      details: error,
    },
    status
  );
}
